<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="balance-scale"/> {{ $t('pageTitle') }}</h1>
      </div>
    </div>

    <div class="row">
      <!-- Bộ lọc -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <label class="mr-1 mb-1">{{ $t('period') }}</label>
            <date-picker v-model="period" type="month" value-type="YYYY-MM" format="MM/YYYY"
                         :lang="$i18n.locale" :clearable="false" :placeholder="$t('selectMonth')"
                         class="mr-2 mb-1"></date-picker>
            <label class="mr-1 mb-1">{{ $t('provider') }}</label>
            <b-form-select class="mr-2 mb-1" v-model="gateway" :options="gatewayOptions" style="width: 200px"></b-form-select>
            <button @click="loadPivot" class="btn btn-primary mr-1 mb-1" :disabled="loading">
              <font-awesome-icon icon="sync" :spin="loading"/>&nbsp;{{ $t('loadReport') }}
            </button>
            <label class="btn btn-outline-success is-border-1 mr-1 mb-1" :class="{disabled: importing}">
              <font-awesome-icon icon="file-upload"/>&nbsp;{{ $t('importProviderFile') }}
              <input type="file" accept=".xlsx" hidden @change="onFile" :disabled="importing"/>
            </label>
            <button v-if="saved" @click="exportExcel" class="btn btn-outline-info is-border-1 mr-1 mb-1" :disabled="exporting">
              <font-awesome-icon icon="file-excel"/>&nbsp;{{ $t('export') }}
            </button>
          </div>
          <b-alert show variant="info" class="small mt-2 mb-0">{{ $t('reconcileNote') }}</b-alert>
        </div>
      </div>

      <!-- Trạng thái + thông tin import/chốt -->
      <div class="col-12" v-if="saved">
        <div class="tile d-flex flex-wrap align-items-center" style="gap:.5rem">
          <span class="badge" :class="saved.status === 'confirmed' ? 'badge-success' : 'badge-warning'">
            {{ saved.status === 'confirmed' ? $t('confirmed') : $t('draft') }}
          </span>
          <span class="small text-gray-999" v-if="saved.importedBy">
            {{ $t('importedBy') }}: <b>{{ saved.importedBy.name }}</b>
            <span v-if="saved.updateTime">— {{ saved.updateTime | moment('DD/MM/YYYY HH:mm') }}</span>
          </span>
          <span class="small text-success" v-if="saved.status === 'confirmed' && saved.confirmedBy">
            <font-awesome-icon icon="check-circle"/> {{ $t('confirmedBy') }}: <b>{{ saved.confirmedBy.name }}</b>
            <span v-if="saved.confirmTime">— {{ saved.confirmTime | moment('DD/MM/YYYY HH:mm') }}</span>
          </span>
          <span class="ml-auto">
            <button v-if="saved.status !== 'confirmed'" @click="confirm(true)" class="btn btn-sm btn-success" :disabled="confirming">
              <font-awesome-icon icon="check"/>&nbsp;{{ $t('confirmBtn') }}
            </button>
            <button v-else @click="confirm(false)" class="btn btn-sm btn-outline-secondary is-border-1" :disabled="confirming">
              {{ $t('unconfirmBtn') }}
            </button>
          </span>
        </div>
      </div>

      <!-- Băng tổng hợp -->
      <div class="col-12" v-if="saved">
        <div class="row">
          <div class="col-md-3 col-6">
            <div class="widget-small primary tile"><div class="info">
              <h4>{{ $t('systemRevenue') }}</h4><p><b>{{ saved.systemTotals.revenue | numFormat('0,0') }}đ</b></p>
              <small>{{ saved.systemTotals.billableCount | numFormat('0,0') }} {{ $t('billableMsg') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small warning tile"><div class="info">
              <h4>{{ $t('providerPayable') }}</h4><p><b>{{ saved.providerTotals.payable | numFormat('0,0') }}đ</b></p>
              <small>{{ saved.providerTotals.billableCount | numFormat('0,0') }} {{ $t('billableMsg') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="margin >= 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('grossMargin') }}</h4><p><b>{{ margin | numFormat('0,0') }}đ</b></p>
              <small>{{ $t('marginNote') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="totalDiffCount === 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('qtyDiff') }}</h4><p><b>{{ totalDiffCount | numFormat('+0,0') }}</b></p>
              <small>{{ totalDiffCount === 0 ? $t('matched') : $t('needReview') }}</small>
            </div></div>
          </div>
        </div>
      </div>

      <!-- Đối soát TIỀN (khớp hoá đơn NCC) -->
      <div class="col-lg-5 col-12" v-if="saved">
        <div class="tile">
          <h5 class="mb-2">{{ $t('moneyRecon') }}</h5>
          <table class="table table-sm mb-0">
            <tbody>
              <tr><td>{{ $t('grossAmount') }}</td><td class="text-right">{{ saved.providerTotals.grossAmount | numFormat('0,0') }}đ</td></tr>
              <tr><td>{{ $t('discount') }}</td><td class="text-right text-danger">− {{ saved.providerTotals.discount | numFormat('0,0') }}đ</td></tr>
              <tr><td>{{ $t('bonus') }}</td><td class="text-right text-danger">− {{ saved.providerTotals.bonus | numFormat('0,0') }}đ</td></tr>
              <tr><td>{{ $t('vat') }}</td><td class="text-right">+ {{ saved.providerTotals.vat | numFormat('0,0') }}đ</td></tr>
              <tr class="font-weight-bold"><td>{{ $t('providerPayable') }}</td><td class="text-right">{{ saved.providerTotals.payable | numFormat('0,0') }}đ</td></tr>
              <tr><td class="text-primary">{{ $t('systemRevenue') }}</td><td class="text-right text-primary">{{ saved.systemTotals.revenue | numFormat('0,0') }}đ</td></tr>
              <tr class="font-weight-bold" :class="margin >= 0 ? 'text-success' : 'text-danger'">
                <td>{{ $t('grossMargin') }}</td><td class="text-right">{{ margin | numFormat('0,0') }}đ</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Context giải thích lệch -->
        <b-alert show variant="light" class="small border" v-if="saved.systemTotals.sentTotal">
          <b>{{ $t('contextTitle') }}:</b>
          {{ $t('ctxSent') }} <b>{{ saved.systemTotals.sentTotal | numFormat('0,0') }}</b> ·
          {{ $t('ctxCharged') }} <b>{{ saved.systemTotals.billableCount | numFormat('0,0') }}</b> ·
          {{ $t('ctxFail') }} <b>{{ saved.systemTotals.failCount | numFormat('0,0') }}</b>
          <div class="text-gray-999 mt-1">{{ $t('diffLegend') }}</div>
        </b-alert>
      </div>

      <!-- Bảng đối soát SẢN LƯỢNG theo template -->
      <div class="col-lg-7 col-12" v-if="saved">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-4">{{ $t('oa') }} / {{ $t('templateId') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-2 text-right">{{ $t('providerCount') }}</div>
            <div class="col-lg-1 text-right">{{ $t('diff') }}</div>
            <div class="col-lg-3 text-right">{{ $t('providerAmount') }}</div>
          </div>
          <div class="row" v-for="(l, i) in saved.lines" :key="i" :class="{'bg-danger-soft': l.diffCount !== 0}">
            <div class="col-lg-4">
              <span>{{ l.oaName }}</span>
              <span v-if="l.source === 'providerOnly'" class="badge badge-warning ml-1">{{ $t('providerOnly') }}</span>
              <span v-if="l.source === 'systemOnly'" class="badge badge-info ml-1">{{ $t('systemOnly') }}</span>
              <p class="text-gray-999 small mb-0">{{ l.templateRefId || '—' }}<span v-if="l.templateName"> · {{ l.templateName }}</span></p>
            </div>
            <div class="col-lg-2 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-2 text-right">{{ l.providerCount | numFormat('0,0') }}</div>
            <div class="col-lg-1 text-right" :class="l.diffCount === 0 ? 'text-success' : 'text-danger font-weight-bold'">
              {{ l.diffCount | numFormat('+0,0') }}
            </div>
            <div class="col-lg-3 text-right">
              {{ l.providerAmount | numFormat('0,0') }}
              <button class="btn btn-sm btn-link p-0 ml-1" v-if="l.templateRefId" @click="drill(l)" :title="$t('viewDetail')">
                <font-awesome-icon icon="search"/>
              </button>
            </div>
          </div>
          <div class="row c-smart-table__title">
            <div class="col-lg-4"><b>{{ $t('total') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ savedTotals.systemCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ savedTotals.providerCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-1 text-right" :class="savedTotals.diffCount === 0 ? 'text-success' : 'text-danger'"><b>{{ savedTotals.diffCount | numFormat('+0,0') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ savedTotals.providerAmount | numFormat('0,0') }}đ</b></div>
          </div>
        </div>
      </div>

      <!-- Pivot tự sinh từ DB (chưa import file) -->
      <div class="col-12" v-if="!saved && pivotLines.length">
        <b-alert show variant="secondary" class="small">
          {{ $t('pivotOnlyNote') }}
          <span v-if="pivotTotals.sentTotal"> — {{ $t('ctxSent') }} <b>{{ pivotTotals.sentTotal | numFormat('0,0') }}</b>,
            {{ $t('ctxCharged') }} <b>{{ pivotTotals.billableCount | numFormat('0,0') }}</b>,
            {{ $t('ctxFail') }} <b>{{ pivotTotals.failCount | numFormat('0,0') }}</b></span>
        </b-alert>
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-4">{{ $t('oa') }}</div>
            <div class="col-lg-3">{{ $t('templateId') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-3 text-right">{{ $t('systemRevenueShort') }}</div>
          </div>
          <div class="row" v-for="(l, i) in pivotLines" :key="i">
            <div class="col-lg-4">{{ l.oaName }}<p class="text-gray-999 small mb-0" v-if="l.templateName">{{ l.templateName }}</p></div>
            <div class="col-lg-3">{{ l.templateRefId || '—' }}</div>
            <div class="col-lg-2 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-3 text-right">{{ l.systemRevenue | numFormat('0,0') }}đ</div>
          </div>
          <div class="row c-smart-table__title">
            <div class="col-lg-7"><b>{{ $t('total') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ pivotTotals.billableCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ pivotTotals.revenue | numFormat('0,0') }}đ</b></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-danger" v-if="!saved && !pivotLines.length && loaded">{{ $t('noData') }}</div>

      <!-- Lịch sử đối soát -->
      <div class="col-12" v-if="history.length">
        <div class="tile">
          <h5 class="mb-2">{{ $t('history') }}</h5>
          <table class="table table-sm table-hover mb-0">
            <thead><tr>
              <th>{{ $t('period') }}</th><th>{{ $t('provider') }}</th>
              <th class="text-right">{{ $t('providerPayable') }}</th>
              <th class="text-right">{{ $t('systemRevenue') }}</th>
              <th class="text-right">{{ $t('grossMargin') }}</th>
              <th class="text-right">{{ $t('qtyDiff') }}</th>
              <th>{{ $t('status') }}</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="h in history" :key="h._id" style="cursor:pointer" @click="openHistory(h)">
                <td><b>{{ formatPeriod(h.period) }}</b></td>
                <td>{{ h.gateway ? h.gateway.name : $t('allProviders') }}</td>
                <td class="text-right">{{ h.providerTotals.payable | numFormat('0,0') }}đ</td>
                <td class="text-right">{{ h.systemTotals.revenue | numFormat('0,0') }}đ</td>
                <td class="text-right" :class="(h.systemTotals.revenue - h.providerTotals.payable) >= 0 ? 'text-success' : 'text-danger'">
                  {{ (h.systemTotals.revenue - h.providerTotals.payable) | numFormat('0,0') }}đ
                </td>
                <td class="text-right" :class="(h.systemTotals.billableCount - h.providerTotals.billableCount) === 0 ? 'text-success' : 'text-danger'">
                  {{ (h.systemTotals.billableCount - h.providerTotals.billableCount) | numFormat('+0,0') }}
                </td>
                <td><span class="badge" :class="h.status === 'confirmed' ? 'badge-success' : 'badge-warning'">{{ h.status === 'confirmed' ? $t('confirmed') : $t('draft') }}</span></td>
                <td class="text-right"><font-awesome-icon icon="chevron-right" class="text-gray-999"/></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal drill-down từng tin -->
    <b-modal v-model="showDrill" :title="drillTitle" size="lg" hide-footer>
      <div class="text-center" v-if="drillLoading"><font-awesome-icon icon="spinner" spin/></div>
      <template v-else>
        <table class="table table-sm">
          <thead><tr>
            <th>{{ $t('phone') }}</th><th>{{ $t('client') }}</th><th>{{ $t('sendTime') }}</th>
            <th>{{ $t('delivery') }}</th><th class="text-right">{{ $t('fee') }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="z in drillItems" :key="z._id">
              <td>{{ z.phone }}</td>
              <td>{{ z.client ? z.client.companyName : '—' }}</td>
              <td>{{ z.sendTime | moment('DD/MM/YY HH:mm') }}</td>
              <td>
                <span v-if="z.receiveTime" class="badge badge-success">{{ $t('delivered') }}</span>
                <span v-else class="badge badge-secondary">{{ $t('notConfirmed') }}</span>
              </td>
              <td class="text-right">{{ z.chargedFee | numFormat('0,0') }}đ</td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex align-items-center justify-content-between">
          <small class="text-gray-999">{{ $t('totalMsg', [numeralFmt(drillCount)]) }}</small>
          <b-pagination v-if="drillCount > drillPageSize" size="sm" class="mb-0"
                        v-model="drillPage" :total-rows="drillCount" :per-page="drillPageSize"
                        @input="loadDrill"></b-pagination>
        </div>
      </template>
    </b-modal>
  </main>
</template>

<script>
import Excel from 'exceljs';
import {saveAs} from 'file-saver';
import moment from 'moment';
import numeral from 'numeral';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: {DatePicker},
  data() {
    return {
      period: moment().format('YYYY-MM'),
      gateway: '',
      gateways: [],
      loading: false,
      loaded: false,
      importing: false,
      exporting: false,
      confirming: false,
      pivotLines: [],
      pivotTotals: {billableCount: 0, revenue: 0, failCount: 0, sentTotal: 0},
      saved: null,
      history: [],
      showDrill: false,
      drillLoading: false,
      drillItems: [],
      drillCount: 0,
      drillPage: 1,
      drillPageSize: 50,
      drillTemplateRefId: null,
      drillTitle: '',
    };
  },
  computed: {
    gatewayOptions() {
      return [{value: '', text: this.$t('allProviders')}, ...this.gateways.map(g => ({value: g._id, text: g.name}))];
    },
    margin() {
      if (!this.saved) return 0;
      return (this.saved.systemTotals.revenue || 0) - (this.saved.providerTotals.payable || 0);
    },
    totalDiffCount() {
      return this.saved ? this.saved.lines.reduce((s, l) => s + (l.diffCount || 0), 0) : 0;
    },
    savedTotals() {
      const t = {systemCount: 0, providerCount: 0, diffCount: 0, providerAmount: 0, systemRevenue: 0};
      if (this.saved) {
        for (const l of this.saved.lines) {
          t.systemCount += l.systemCount || 0;
          t.providerCount += l.providerCount || 0;
          t.diffCount += l.diffCount || 0;
          t.providerAmount += l.providerAmount || 0;
          t.systemRevenue += l.systemRevenue || 0;
        }
      }
      return t;
    },
  },
  mounted() {
    this.loadGateways();
    this.loadHistory();
  },
  methods: {
    numeralFmt(n) {
      return numeral(n).format('0,0');
    },
    formatPeriod(p) {
      return p ? moment(p, 'YYYY-MM').format('MM/YYYY') : '';
    },
    async loadGateways() {
      try {
        const {data} = await this.$service.get('/zns-gateway', {params: {pageSize: 100}});
        this.gateways = (data && data.docs) || [];
      } catch (e) {
        this.gateways = [];
      }
    },
    async loadHistory() {
      try {
        const {data} = await this.$service.get('/zns-reconcile', {params: {pageSize: 24}});
        this.history = data.data || [];
      } catch (e) {
        this.history = [];
      }
    },
    async loadPivot() {
      if (!this.period) return;
      const loader = this.$loading.show();
      this.loading = true;
      try {
        const {data} = await this.$service.get('/zns-reconcile/pivot', {params: {period: this.period, gateway: this.gateway}});
        const d = data.data || {};
        this.pivotLines = d.lines || [];
        this.pivotTotals = d.totals || {billableCount: 0, revenue: 0, failCount: 0, sentTotal: 0};
        this.saved = d.saved || null;
        this.loaded = true;
      } catch (e) {
        this.$swal({text: this.errMsg(e, 'Lỗi tải báo cáo'), icon: 'error'});
      } finally {
        this.loading = false;
        loader.hide();
      }
    },
    async onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const loader = this.$loading.show();
      this.importing = true;
      try {
        const buffer = await file.arrayBuffer();
        const parsed = await this.parseProviderFile(buffer);
        if (!parsed.providerLines.length) throw new Error(this.$t('parseNoLines'));
        const {data} = await this.$service.post('/zns-reconcile/import', {
          period: this.period,
          gateway: this.gateway,
          importedFileName: file.name,
          providerTotals: parsed.providerTotals,
          providerLines: parsed.providerLines,
        });
        this.saved = data.data;
        this.loaded = true;
        await this.loadHistory();
        this.$swal({text: this.$t('importSuccess'), icon: 'success', timer: 1500, showConfirmButton: false});
      } catch (err) {
        this.$swal({text: this.errMsg(err, 'Lỗi import file'), icon: 'error'});
      } finally {
        this.importing = false;
        loader.hide();
      }
    },
    async confirm(confirmed) {
      if (!this.saved) return;
      this.confirming = true;
      try {
        const {data} = await this.$service.patch(`/zns-reconcile/${this.saved._id}/confirm`, {confirmed});
        this.saved = data.data;
        await this.loadHistory();
      } catch (e) {
        this.$swal({text: this.errMsg(e, 'Lỗi cập nhật trạng thái'), icon: 'error'});
      } finally {
        this.confirming = false;
      }
    },
    openHistory(h) {
      this.period = h.period;
      this.gateway = h.gateway ? h.gateway._id : '';
      this.loadPivot();
      window.scrollTo({top: 0, behavior: 'smooth'});
    },
    // ---- Drill-down ----
    drill(line) {
      if (!this.saved || !line.templateRefId) return;
      this.drillTemplateRefId = line.templateRefId;
      this.drillTitle = `${line.oaName} — ${line.templateRefId}`;
      this.drillPage = 1;
      this.showDrill = true;
      this.loadDrill();
    },
    async loadDrill() {
      this.drillLoading = true;
      try {
        const {data} = await this.$service.get(`/zns-reconcile/${this.saved._id}/detail`, {
          params: {templateRefId: this.drillTemplateRefId, page: this.drillPage, pageSize: this.drillPageSize},
        });
        this.drillItems = data.data || [];
        this.drillCount = data.itemCount || 0;
      } catch (e) {
        this.$swal({text: 'Lỗi tải chi tiết', icon: 'error'});
      } finally {
        this.drillLoading = false;
      }
    },
    // ---- Export ----
    async exportExcel() {
      if (!this.saved) return;
      this.exporting = true;
      try {
        const wb = new Excel.Workbook();
        const s = this.saved;
        const ws = wb.addWorksheet(this.$t('reconcileSheet'));
        ws.columns = [
          {header: this.$t('oa'), key: 'oa', width: 28},
          {header: this.$t('templateId'), key: 'ref', width: 14},
          {header: this.$t('templateName'), key: 'name', width: 30},
          {header: this.$t('systemCount'), key: 'sc', width: 14},
          {header: this.$t('providerCount'), key: 'pc', width: 14},
          {header: this.$t('diff'), key: 'd', width: 10},
          {header: this.$t('providerAmount'), key: 'pa', width: 16},
          {header: this.$t('systemRevenueShort'), key: 'sr', width: 16},
        ];
        ws.getRow(1).font = {bold: true};
        for (const l of s.lines) {
          ws.addRow({oa: l.oaName, ref: l.templateRefId || '', name: l.templateName || '',
            sc: l.systemCount, pc: l.providerCount, d: l.diffCount, pa: l.providerAmount, sr: l.systemRevenue});
        }
        const t = this.savedTotals;
        const totalRow = ws.addRow({oa: this.$t('total'), ref: '', name: '',
          sc: t.systemCount, pc: t.providerCount, d: t.diffCount, pa: t.providerAmount, sr: t.systemRevenue});
        totalRow.font = {bold: true};

        const ws2 = wb.addWorksheet(this.$t('moneyRecon'));
        ws2.columns = [{header: '', key: 'k', width: 30}, {header: '', key: 'v', width: 18}];
        const rows2 = [
          [this.$t('grossAmount'), s.providerTotals.grossAmount],
          [this.$t('discount'), -s.providerTotals.discount],
          [this.$t('bonus'), -s.providerTotals.bonus],
          [this.$t('vat'), s.providerTotals.vat],
          [this.$t('providerPayable'), s.providerTotals.payable],
          [this.$t('systemRevenue'), s.systemTotals.revenue],
          [this.$t('grossMargin'), this.margin],
        ];
        rows2.forEach(r => ws2.addRow({k: r[0], v: r[1]}));

        const buffer = await wb.xlsx.writeBuffer();
        const gw = s.gateway ? s.gateway.name : 'all';
        saveAs(new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
          `doi-soat-zbs-${s.period}-${gw}.xlsx`);
      } catch (e) {
        this.$swal({text: 'Lỗi xuất Excel', icon: 'error'});
      } finally {
        this.exporting = false;
      }
    },
    errMsg(e, fallback) {
      return (e.response && e.response.data && e.response.data.message) || e.message || fallback;
    },
    // ---- Parse file provider (exceljs) ----
    cellRaw(cell) {
      if (cell == null) return null;
      const v = cell.value;
      if (v == null) return null;
      if (typeof v === 'object') {
        if (v.result != null) return v.result;
        if (v.text != null) return v.text;
        if (v.richText) return v.richText.map(r => r.text).join('');
        return null;
      }
      return v;
    },
    cellNum(cell) {
      const v = this.cellRaw(cell);
      if (typeof v === 'number') return v;
      if (v == null) return 0;
      const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    },
    cellStr(cell) {
      const v = this.cellRaw(cell);
      return v == null ? '' : String(v).trim();
    },
    async parseProviderFile(buffer) {
      const wb = new Excel.Workbook();
      await wb.xlsx.load(buffer);
      const wsTmpl = wb.worksheets.find(w => /t[ổo]ng theo template/i.test(w.name)) || wb.getWorksheet('Tổng theo Template');
      if (!wsTmpl) throw new Error(this.$t('parseNoSheet'));

      let headerRow = 0, col = {oa: 2, refId: 3, billable: 6, amount: 7};
      wsTmpl.eachRow((row, rn) => {
        if (headerRow) return;
        const cells = row.values.map(v => (v && typeof v === 'object' && v.result != null ? v.result : v));
        const joined = cells.map(c => String(c || '').toLowerCase()).join('|');
        if (joined.includes('id mẫu tin') || joined.includes('id mau tin')) {
          headerRow = rn;
          cells.forEach((c, idx) => {
            const st = String(c || '').toLowerCase();
            if (st.includes('tên oa') || st.includes('ten oa')) col.oa = idx;
            else if (st.includes('id mẫu') || st.includes('id mau')) col.refId = idx;
            else if (st.includes('tính phí') || st.includes('tinh phi')) col.billable = idx;
            else if (st.includes('thành tiền') || st.includes('thanh tien')) col.amount = idx;
          });
        }
      });

      const providerLines = [];
      wsTmpl.eachRow((row, rn) => {
        if (!headerRow || rn <= headerRow) return;
        const refId = this.cellStr(row.getCell(col.refId));
        const count = this.cellNum(row.getCell(col.billable));
        if (!refId && !count) return;
        providerLines.push({
          oaName: this.cellStr(row.getCell(col.oa)),
          templateRefId: refId,
          count,
          amount: this.cellNum(row.getCell(col.amount)),
        });
      });

      const providerTotals = {
        billableCount: providerLines.reduce((s, l) => s + l.count, 0),
        grossAmount: providerLines.reduce((s, l) => s + l.amount, 0),
        discount: 0, bonus: 0, vat: 0, payable: 0,
      };
      const wsDsc = wb.worksheets.find(w => /dsc/i.test(w.name));
      if (wsDsc) {
        wsDsc.eachRow((row) => {
          const label = this.cellStr(row.getCell(1)).toLowerCase();
          const val = this.cellNum(row.getCell(2));
          if (!label) return;
          if (label === 'doanh thu') providerTotals.grossAmount = val;
          else if (label.startsWith('chiết khấu') || label.startsWith('chiet khau')) providerTotals.discount = val;
          else if (label.startsWith('giá trị tặng thêm') || label.startsWith('gia tri tang them')) providerTotals.bonus = val;
          else if (label === 'vat') providerTotals.vat = val;
          else if (label.startsWith('số tiền phải thanh toán') || label.startsWith('so tien phai thanh toan')) providerTotals.payable = val;
        });
      }
      if (!providerTotals.payable) {
        providerTotals.payable = providerTotals.grossAmount - providerTotals.discount - providerTotals.bonus + providerTotals.vat;
      }
      return {providerLines, providerTotals};
    },
  },
  i18n: {
    messages: {
      en: {
        pageTitle: 'ZBS reconciliation',
        period: 'Period', selectMonth: 'Select month', provider: 'Provider', allProviders: 'All providers',
        loadReport: 'Load report', importProviderFile: 'Import provider file', export: 'Export',
        reconcileNote: 'Reconciliation groups billable ZBS by send time (matches provider "transaction date"). Cost/discount/VAT come from the provider file only.',
        pivotOnlyNote: 'DB figures only (quantity + revenue). Import the provider file to reconcile cost & margin.',
        systemRevenue: 'Callio revenue', providerPayable: 'Provider payable', grossMargin: 'Gross margin',
        marginNote: 'revenue − payable', qtyDiff: 'Qty diff', matched: 'Matched', needReview: 'Needs review',
        billableMsg: 'billable msg', oa: 'OA', templateId: 'Template ID', templateName: 'Template name',
        systemCount: 'Callio count', providerCount: 'Provider count', diff: 'Diff', providerAmount: 'Provider amount',
        systemRevenueShort: 'Revenue', providerOnly: 'Provider only', systemOnly: 'Callio only',
        viewDetail: 'View messages', total: 'Total', noData: 'No data for this period',
        phone: 'Phone', client: 'Client', sendTime: 'Send time', fee: 'Fee', delivery: 'Delivery',
        delivered: 'Delivered', notConfirmed: 'Not confirmed', totalMsg: 'Total {0} messages',
        importSuccess: 'Imported & reconciled',
        parseNoSheet: 'File missing sheet "Tổng theo Template"', parseNoLines: 'No template rows parsed from file',
        moneyRecon: 'Money reconciliation', grossAmount: 'Gross revenue', discount: 'Discount', bonus: 'Bonus value', vat: 'VAT',
        contextTitle: 'Context', ctxSent: 'sent', ctxCharged: 'charged', ctxFail: 'failed',
        diffLegend: 'Diff (+) = messages Callio charged but the provider did not bill — most likely sent but not delivered. Delivery tracking (receiveTime) is currently incomplete.',
        history: 'Reconciliation history', status: 'Status', draft: 'Draft', confirmed: 'Confirmed',
        importedBy: 'Imported by', confirmedBy: 'Confirmed by', confirmBtn: 'Confirm', unconfirmBtn: 'Unconfirm',
        reconcileSheet: 'Reconciliation',
      },
      vi: {
        pageTitle: 'Đối soát ZBS',
        period: 'Kỳ', selectMonth: 'Chọn tháng', provider: 'Nhà cung cấp', allProviders: 'Tất cả nhà cung cấp',
        loadReport: 'Tải báo cáo', importProviderFile: 'Import file nhà cung cấp', export: 'Xuất Excel',
        reconcileNote: 'Đối soát gom tin ZBS tính phí theo thời điểm gửi (khớp "Ngày giao dịch" của nhà cung cấp). Giá vốn/chiết khấu/VAT chỉ lấy từ file nhà cung cấp.',
        pivotOnlyNote: 'Chỉ số liệu từ DB (sản lượng + doanh thu). Import file nhà cung cấp để đối soát giá vốn & lợi nhuận.',
        systemRevenue: 'Doanh thu Callio', providerPayable: 'Phải trả NCC', grossMargin: 'Lợi nhuận gộp',
        marginNote: 'doanh thu − phải trả', qtyDiff: 'Lệch sản lượng', matched: 'Khớp', needReview: 'Cần rà soát',
        billableMsg: 'tin tính phí', oa: 'OA', templateId: 'ID mẫu tin', templateName: 'Tên mẫu tin',
        systemCount: 'Callio ghi nhận', providerCount: 'NCC tính', diff: 'Lệch', providerAmount: 'NCC thành tiền',
        systemRevenueShort: 'Doanh thu', providerOnly: 'Chỉ có ở NCC', systemOnly: 'Chỉ có ở Callio',
        viewDetail: 'Xem chi tiết tin', total: 'Tổng', noData: 'Không có dữ liệu trong kỳ',
        phone: 'Số điện thoại', client: 'Khách hàng', sendTime: 'Thời điểm gửi', fee: 'Phí', delivery: 'Giao tin',
        delivered: 'Đã giao', notConfirmed: 'Chưa xác nhận', totalMsg: 'Tổng {0} tin',
        importSuccess: 'Đã import & đối soát',
        parseNoSheet: 'File thiếu sheet "Tổng theo Template"', parseNoLines: 'Không đọc được dòng mẫu tin nào từ file',
        moneyRecon: 'Đối soát tiền', grossAmount: 'Doanh thu gốc', discount: 'Chiết khấu', bonus: 'Giá trị tặng thêm', vat: 'VAT',
        contextTitle: 'Bối cảnh', ctxSent: 'đã gửi', ctxCharged: 'tính phí', ctxFail: 'gửi lỗi',
        diffLegend: 'Lệch (+) = tin Callio đã tính phí nhưng NCC không tính — nhiều khả năng gửi đi nhưng không giao tới đích. Theo dõi "đã giao" (receiveTime) hiện đang thiếu dữ liệu.',
        history: 'Lịch sử đối soát', status: 'Trạng thái', draft: 'Nháp', confirmed: 'Đã chốt',
        importedBy: 'Người import', confirmedBy: 'Người chốt', confirmBtn: 'Chốt đối soát', unconfirmBtn: 'Bỏ chốt',
        reconcileSheet: 'Đối soát',
      },
    },
  },
};
</script>

<style scoped>
.bg-danger-soft { background-color: rgba(220, 53, 69, 0.08); }
</style>
